import type {ContentPart, CopilotChatMessage, CopilotChatReference, SkillExecution} from '@github-ui/copilot-chat-types'

import type {FunctionMetadata} from '../components/FunctionLoadingUtils'

// ---------------------------------------------------------------------------
// Content chunks: interleave consecutive tool-call parts into a single chunk
// while preserving text part order within an assistant message.
// ---------------------------------------------------------------------------

export type ContentChunk =
  | {kind: 'text'; partIndex: number; content: string}
  | {kind: 'toolGroup'; partIndex: number; skills: SkillExecution[]}

export type ContentChunkLayout = {
  chunks: ContentChunk[]
  firstTextIndex: number
  lastTextIndex: number
}

export function buildContentChunks(contentParts: ContentPart[]): ContentChunkLayout {
  let firstTextIndex = -1
  let lastTextIndex = -1
  const chunks: ContentChunk[] = []

  for (let i = 0; i < contentParts.length; i++) {
    const part = contentParts[i]
    if (!part) continue
    if (part.type === 'text') {
      // Trim surrounding whitespace so a streaming-only trailing newline (which the
      // markdown renderer turns into an empty <p>) can't change the spacing between
      // the text and an adjacent tool group between the streaming and finished states.
      const content = part.content?.trim()
      if (!content) continue
      if (firstTextIndex === -1) firstTextIndex = i
      lastTextIndex = i
      chunks.push({kind: 'text', partIndex: i, content})
    } else {
      // Only fuse consecutive tool calls that invoke the same skill so unrelated
      // tool types aren't grouped under one summary.
      const last = chunks[chunks.length - 1]
      if (last && last.kind === 'toolGroup' && last.skills[0]?.slug === part.skillExecution.slug) {
        last.skills.push(part.skillExecution)
      } else {
        chunks.push({kind: 'toolGroup', partIndex: i, skills: [part.skillExecution]})
      }
    }
  }

  return {chunks, firstTextIndex, lastTextIndex}
}

// ---------------------------------------------------------------------------
// Tool-group summary: derive aggregate status + natural-language trigger label.
// ---------------------------------------------------------------------------

export type ToolGroupStatus = 'running' | 'completed' | 'error' | 'partial'

export function deriveToolGroupStatus(skillExecutions: SkillExecution[]): ToolGroupStatus {
  if (skillExecutions.length === 0) return 'completed'
  const hasRunning = skillExecutions.some(s => s.status === 'started' || s.status === 'progress')
  if (hasRunning) return 'running'
  const errorCount = skillExecutions.filter(s => s.status === 'error').length
  if (errorCount === 0) return 'completed'
  if (errorCount === skillExecutions.length) return 'error'
  return 'partial'
}

function leadText(skill: SkillExecution, metadata: FunctionMetadata | undefined, status: ToolGroupStatus): string {
  const isRunning = skill.status === 'started' || skill.status === 'progress'
  const lead = isRunning ? metadata?.loadingText : metadata?.completedText
  if (lead && lead.trim().length > 0) return lead
  return status === 'running' ? 'Running tool call' : 'Ran tool call'
}

export interface ToolGroupSummaryInput {
  skillExecutions: SkillExecution[]
  metadataFor: (skill: SkillExecution) => FunctionMetadata | undefined
}

export function summarizeToolGroup({skillExecutions, metadataFor}: ToolGroupSummaryInput): string {
  const first = skillExecutions[0]
  if (!first) return ''
  const status = deriveToolGroupStatus(skillExecutions)
  const lead = leadText(first, metadataFor(first), status)
  const others = skillExecutions.length - 1
  if (others === 0) return lead
  if (others === 1) return `${lead} and 1 other tool call`
  return `${lead} and ${others} other tool calls`
}

// ---------------------------------------------------------------------------
// Cross-turn fusion: mirrors github-app/4873's rule that consecutive tool-only
// turns collapse into one panel. Carrier is the LAST message in the run so
// regenerate/feedback affordances act on the most recent turn.
// ---------------------------------------------------------------------------

export function fuseAssistantMessages(messages: readonly CopilotChatMessage[]): CopilotChatMessage[] {
  const out: CopilotChatMessage[] = []
  let run: CopilotChatMessage[] = []

  const flushRun = () => {
    if (run.length === 0) return
    const single = run.length === 1 ? run[0] : undefined
    if (single) {
      out.push(single)
    } else {
      out.push(mergeAssistantRun(run))
    }
    run = []
  }

  for (const message of messages) {
    if (message.role === 'assistant' && message.contentParts && message.contentParts.length > 0) {
      run.push(message)
    } else {
      flushRun()
      out.push(message)
    }
  }
  flushRun()

  return out
}

function mergeAssistantRun(run: readonly CopilotChatMessage[]): CopilotChatMessage {
  const carrier = run[run.length - 1] ?? run[0]
  if (!carrier) throw new Error('mergeAssistantRun called with empty run')
  const contentParts: ContentPart[] = []
  const references: CopilotChatReference[] = []
  const skillExecutions = []

  for (const message of run) {
    if (message.contentParts) contentParts.push(...message.contentParts)
    if (message.references) references.push(...message.references)
    if (message.skillExecutions) skillExecutions.push(...message.skillExecutions)
  }

  return {
    ...carrier,
    contentParts,
    references: references.length > 0 ? references : carrier.references,
    skillExecutions: skillExecutions.length > 0 ? skillExecutions : carrier.skillExecutions,
  }
}
